import React from 'react';
import i18n from 'util/i18n';
import { formConfig } from 'config/forms';
import { getNumberOfYearsString } from 'helpers/text';

import { Summary, DateSummary } from 'components/Summary';
import { SUBSTANCE_USE, SUBSTANCE_USE_ALCOHOL_VOLUNTARY } from 'config/formSections/substanceUse';
import Subsection from 'components/Section/shared/Subsection';
import connectSubsection from 'components/Section/shared/SubsectionConnector';
import { EmptyListInstance } from 'forms/shared/models';
import Branch from 'components/Form/Branch';
import Show from 'components/Form/Show';
import Accordion from 'components/Form/Accordion';
import Comments from 'components/Form/Comments';
import { getDuplicateNamesAndAddresses } from 'helpers/people';
import { alcoholVoluntaryCounselingAddressNameFields } from 'constants/enums/people';
import ErrorMessage from 'components/ErrorMessage';
import Field from 'components/Form/Field';
import { isPVQForm } from 'helpers/form';
import VoluntaryCounseling from './VoluntaryCounseling';

const sectionConfig = {
  key: SUBSTANCE_USE_ALCOHOL_VOLUNTARY.key,
  section: SUBSTANCE_USE.name,
  store: SUBSTANCE_USE.store,
  subsection: SUBSTANCE_USE_ALCOHOL_VOLUNTARY.name,
  storeKey: SUBSTANCE_USE_ALCOHOL_VOLUNTARY.storeKey,
};
export class VoluntaryCounselings extends Subsection {
  constructor(props) {
    super(props);

    const {
      section, subsection, store, storeKey
    } = sectionConfig;

    this.section = section;
    this.subsection = subsection;
    this.store = store;
    this.storeKey = storeKey;
    this.updateList = this.updateList.bind(this);
    this.updateSoughtTreatment = this.updateSoughtTreatment.bind(this);
    this.summary = this.summary.bind(this);
  }

  update = (updateValues) => {
    this.props.onUpdate(this.storeKey, {
      SoughtTreatment: this.props.SoughtTreatment,
      List: this.props.List,
      Comments: this.props.Comments,
      SummaryComments: this.props.SummaryComments,
      ...updateValues,
    });
  };

  updateList = (values) => {
    this.update({
      List: values,
    });
  };

  updateSoughtTreatment = (values) => {
    this.update({
      SoughtTreatment: values,
      List: values.value === 'Yes' ? this.props.List : { items: [], branch: {} },
      SummaryComments: values.value === 'Yes' ? this.props.SummaryComments : {},
    });
  };

  updateComments = (values) => {
    this.update({
      Comments: values,
    });
  };

  updateSummaryComments = (values) => {
    this.update({
      SummaryComments: values,
    });
  };

  summary = (item, index) => {
    const o = (item || {}).Item || {};
    const counselor = o.TreatmentProviderName ? o.TreatmentProviderName.value : '';
    const counselingDates = DateSummary(o.CounselingDates);

    return Summary({
      type: i18n.t('substance.alcohol.voluntaryCounseling.collection.itemType'),
      index,
      left: counselor,
      right: counselingDates,
      placeholder: i18n.t('substance.alcohol.voluntaryCounseling.collection.summary'),
    });
  };

  render() {
    const {
      formType, errors, peopleWhoKnowYou, List
    } = this.props;
    const formTypeConfig = formType && formConfig[formType];
    const years = formTypeConfig && formTypeConfig.SUBSTANCE_ALCOHOL_TREATMENT_YEARS;
    const isPVQ = isPVQForm(this.props.formType);

    let branchLabelCopy;
    if (years === 'EVER') {
      branchLabelCopy = i18n.t('substance.alcohol.heading.voluntaryCounseling');
    } else {
      const numberOfYearsString = getNumberOfYearsString(years);
      branchLabelCopy = i18n.t('substance.alcohol.heading.voluntaryCounselingNum', {
        numberOfYearsString,
      });
    }

    const accordionErrors = errors && errors.filter((e) => e.indexOf('List.accordion') === 0);
    const duplicatePeopleWhoKnowYou = getDuplicateNamesAndAddresses(List?.items, peopleWhoKnowYou, alcoholVoluntaryCounselingAddressNameFields);
    const duplicatePeopleWhoKnowYouError = (
      <Field
        className="no-margin-bottom"
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
        scrollIntoView={this.props.scrollIntoView}
        hideTitle
      >
        <ErrorMessage
          title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.title')}
          message={[
            i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouError.message'),
            ...duplicatePeopleWhoKnowYou.errors.map((item) => ` - ${item}`)
          ]}
        />
      </Field>
    );

    const duplicatePeopleWhoKnowYouWarning = (
      <ErrorMessage
        isWarning
        title={i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.title')}
        message={[
          i18n.t('relationships.people.error.duplicatePeopleWhoKnowYouWarning.message'),
          ...duplicatePeopleWhoKnowYou.warnings.map((item) => ` - ${item}`)
        ]}
      />
    );

    return isPVQ ? (
      <div
        className="section-content voluntary-counselings"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_ALCOHOL_VOLUNTARY.key}
      >
        <h1 className="section-header">{i18n.t('substance.pvq.subsection.alcohol.voluntary')}</h1>
        <Branch
          name="SoughtTreatment"
          attribute="SoughtTreatment"
          label={i18n.t('substance.pvq.alcohol.heading.voluntaryCounseling')}
          labelSize="h4"
          className="sought-treatment"
          {...this.props.SoughtTreatment}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateSoughtTreatment}
          scrollIntoView={this.props.scrollIntoView}
        />
        {this.props.SoughtTreatment.value === 'Yes'
          && (
          <Accordion
            {...this.props.List}
            defaultState={this.props.defaultState}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.pvq.alcohol.voluntaryCounseling.collection.description')}
            appendTitle={i18n.t('substance.pvq.alcohol.voluntaryCounseling.collection.appendTitle')}
            appendLabel={i18n.t('substance.pvq.alcohol.voluntaryCounseling.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
          >
            <VoluntaryCounseling
              name="Item"
              bind
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
              formType={this.props.formType}
            />
          </Accordion>
          )}
      </div>
    ) : (
      <div
        className="section-content voluntary-counselings"
        data-section={SUBSTANCE_USE.key}
        data-subsection={SUBSTANCE_USE_ALCOHOL_VOLUNTARY.key}
      >
        <h1 className="section-header">{i18n.t('substance.subsection.alcohol.voluntary')}</h1>
        <Branch
          name="SoughtTreatment"
          attribute="SoughtTreatment"
          label={branchLabelCopy}
          labelSize="h4"
          className="sought-treatment"
          {...this.props.SoughtTreatment}
          warning
          onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
          required={this.props.required}
          onUpdate={this.updateSoughtTreatment}
          scrollIntoView={this.props.scrollIntoView}
        />
        <Comments
          title={i18n.t('substance.comments.optional')}
          name="Comments"
          className="optional-comment"
          attribute="Comments"
          {...this.props.Comments}
          onUpdate={this.updateComments}
          required={false}
        />
        <Show when={this.props.SoughtTreatment.value === 'Yes'}>
          <Accordion
            defaultState={this.props.defaultState}
            {...this.props.List}
            scrollToBottom={this.props.scrollToBottom}
            summary={this.summary}
            onUpdate={this.updateList}
            onError={this.handleError} // eslint-disable-line @typescript-eslint/unbound-method
            errors={accordionErrors}
            description={i18n.t('substance.alcohol.voluntaryCounseling.collection.description')}
            appendTitle={i18n.t('substance.alcohol.voluntaryCounseling.collection.appendTitle')}
            appendLabel={i18n.t('substance.alcohol.voluntaryCounseling.collection.appendLabel')}
            required={this.props.required}
            scrollIntoView={this.props.scrollIntoView}
            additionalErrors={
              (duplicatePeopleWhoKnowYou.errors.length > 0 && duplicatePeopleWhoKnowYouError)
              || (duplicatePeopleWhoKnowYou.warnings.length > 0 && duplicatePeopleWhoKnowYouWarning)
            }
          >
            <VoluntaryCounseling
              name="Item"
              bind
              addressBooks={this.props.addressBooks}
              dispatch={this.props.dispatch}
              required={this.props.required}
              scrollIntoView={this.props.scrollIntoView}
            />
          </Accordion>
          <Comments
            title={i18n.t('substance.comments.summary')}
            name="SummaryComments"
            className="summary-comment"
            attribute="SummaryComments"
            {...this.props.SummaryComments}
            onUpdate={this.updateSummaryComments}
            required={false}
          />
        </Show>
      </div>
    );
  }
}

VoluntaryCounselings.defaultProps = {
  SoughtTreatment: {},
  List: EmptyListInstance,
  Comments: {},
  SummaryComments: {},
  RecommendedBy: {},
  RecommendedByExplanation: {},
  CounselingDates: {},
  TreatmentProviderName: {},
  TreatmentProviderNameNotApplicable: {},
  TreatmentProviderPersonName: {},
  TreatmentProviderPhones: {},
  TreatmentProviderEmails: {},
  TreatmentProviderAddress: {},
  TreatmentProviderAddressNotApplicable: {},
  PracticeName: {},
  PracticeNameNotApplicable: {},
  ProviderStillPractices: {},
  CompletedTreatment: {},
  NoCompletedTreatmentExplanation: {},
  onError: (value, arr) => arr,
  section: 'substance',
  subsection: 'alcohol/voluntary',
  addressBooks: {},
  dispatch: () => { },
  scrollToBottom: '',
  errors: [],
};

export default connectSubsection(VoluntaryCounselings, sectionConfig);
